import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Text, Tooltip, XAxis, YAxis } from 'recharts';
import { formatNumber } from '../../../core/formatter/formatNumber';
import * as React from 'react';
import { SectionStyle } from '../../../helpers/SectionStyle';

interface StackedDiagramProps extends React.ClassAttributes<any> {
    barProps?: any;
    bars: any;
    data: any;
    containerSize?: any;
    disableLegend?: boolean;
    id?: string;
    legendBottom?: boolean;
    xAxisLabel?: string;
    xAxisProps?: any;
    xAxisTickStyle?: any;
    yAxisLabel?: string;
    yAxisProps?: any;
    fontSize?: string;
}

export class StackedDiagram extends React.Component<StackedDiagramProps, {}> {

    constructor(props: any) {
        super(props);
        this.state = {};
        this.renderLegend = this.renderLegend.bind(this);
        this.renderTooltip = this.renderTooltip.bind(this);
        this.renderTicks = this.renderTicks.bind(this);
    }

    getData(): any[] {
        return (this.props.data || []).map((item: any) => {
            const name: string = item.name || null;
            let values: any = {};

            (item.values || []).map((value: any, index: number) => {
                values = { ...values, [index]: value };
            });
            return { ...(name ? { name } : {}), ...values };
        });
    }

    render(): JSX.Element {
        if (!this.props.data) {
            return null;
        }
        const { fontSize, containerSize } = this.props;
        const containerId: string = typeof SectionStyle.curPrintSectionId === 'number' ? SectionStyle.getCurrentClass(true) : null;
        const Section: any = new SectionStyle(containerId);
        const backgroundColorSectionChart: string = Section.getPropertyValue('--color-section-chart-background');
        const width = containerSize && containerSize.width ? containerSize.width : undefined;
        const height = containerSize && containerSize.height ? containerSize.height : undefined;
        return (
            <ResponsiveContainer width={width} height={height} id={this.props.id}>
                <BarChart
                    data={this.getData()}
                    style={{ fontSize: fontSize || '10pt' }}
                    margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
                >
                    {this.renderXAxis()}
                    {this.renderYAxis()}
                    <Tooltip
                        content={this.renderTooltip}
                        wrapperStyle={{
                            color: '#888',
                            backgroundColor: 'whitesmoke',
                            border: '1px solid #ccc',
                            padding: 5,
                            opacity: 0.9
                        }}
                    />
                    {!this.props.disableLegend &&
                        <Legend
                            layout={this.props.legendBottom ? 'horizontal' : 'vertical'}
                            align={this.props.legendBottom ? 'left' : 'right'}
                            verticalAlign={this.props.legendBottom ? 'bottom' : 'middle'}
                            content={this.renderLegend}
                            wrapperStyle={{ padding: 5 }}
                        />
                    }
                    <CartesianGrid vertical={false} stroke={'#b0b0b0'} fill={backgroundColorSectionChart} />
                    {this.renderBars()}
                </BarChart>
            </ResponsiveContainer>
        );
    }

    renderBarLabel(props: any): JSX.Element {
        return (
            <Text
                x={props.x + 30}
                y={Math.round(props.y + 20)}
                dy={-8}
                fill='#333'
                textAnchor='middle'
            >
                {+props.value > 0 ? formatNumber(props.value, 0) : null}
            </Text>
        );
    }

    renderBars(): JSX.Element[] {
        const props: any = this.props.barProps || {};
        const barSize: number = props.barSize || 60;
        const label: any = props.label ? this.renderBarLabel : null;

        return (this.props.bars || []).map((bar: any, index: number) =>
            <Bar
                key={index}
                dataKey={index}
                label={label}
                barSize={barSize}
                stackId='stack'
                name={bar.name || 'Wert'}
                fill={bar.color || null}
                isAnimationActive={false}
            />
        );
    }

    renderLegend(props: any): JSX.Element {
        const style: React.CSSProperties = this.props.legendBottom
            ? { color: '#666', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'flex-start' }
            : {};

        return (
            <ul style={{ listStyle: 'none', margin: 'auto', padding: 0, ...style }}>
                {(props.payload || [])
                    .reverse()
                    .map((entry: any, index: number) =>
                        <li
                            key={`legend-${index}`}
                            style={{ ...(this.props.legendBottom ? { marginRight: 4, flexDirection: 'row', alignItems: 'center' } : {}) }}
                        >
                            <div style={{ backgroundColor: entry.color, width: 10, height: 10, marginRight: 5, display: 'inline-block' }} />
                            {entry.value}
                        </li>
                    )
                }
            </ul>
        );
    }

    renderTicks(props: any): JSX.Element {
        const xAxisProps: any = this.props.xAxisProps || {};
        const angle: number = xAxisProps.angle || 0;
        const width: number = props.width / props.visibleTicksCount;
        const textAnchor: string = angle ? 'end' : 'middle';
        const verticalAnchor: string = angle ? 'end' : 'start';

        return (
            <g transform={`translate(${props.x},${props.y})`}>
                <Text
                    width={width}
                    x={0}
                    y={0}
                    dy={2}
                    textAnchor={textAnchor}
                    verticalAnchor={verticalAnchor}
                    fill='#666'
                    transform={`rotate(${angle})`}
                    {...(xAxisProps.style || {})}
                >
                    {props.payload.value}
                </Text>
            </g>
        );
    }

    renderTooltip(props: any): JSX.Element {
        const { fontSize } = this.props;
        const style: React.CSSProperties = { margin: 0, padding: '2px 4px' };
        return (
            <ul style={{ fontSize: fontSize || '0.9em', margin: 'auto', padding: 0, listStyle: 'none' }}>
                <li style={style}><strong>{props.label || null}</strong></li>
                {(props.payload || [])
                    .reverse()
                    .map((entry: any, index: number) =>
                        <li key={`tooltip-${index}`} style={style}>
                            <div style={{ backgroundColor: entry.fill, width: 10, height: 10, marginRight: 5, display: 'inline-block' }} />
                            <strong>{entry.name}:</strong> {formatNumber(entry.value)}
                        </li>
                    )
                }
            </ul>
        );
    }

    renderXAxis(): JSX.Element {
        const props: any = this.props.xAxisProps || {};
        const labelValue: any = this.props.xAxisLabel ? { value: this.props.xAxisLabel } : null;
        const height: number = labelValue ? 30 : 10;
        const label: any = labelValue ? { ...labelValue, position: 'insideBottom', offset: 0 } : {};
        return (
            <XAxis
                dataKey={props.dataKey || 'name'}
                height={+props.height || height}
                stroke={props.stroke || '#666'}
                interval={0}
                tick={props.tick === false ? false : this.renderTicks}
                tickLine={props.tickLine || false}
                label={label}
                {...(props.style || {})}
            />
        );
    }

    renderYAxis(): JSX.Element {
        const props: any = this.props.yAxisProps || {};
        const label: any = this.props.yAxisLabel ? { value: this.props.yAxisLabel } : null;
        return (
            <YAxis
                width={+props.width || 70}
                domain={props.domain || [0, 'auto']}
                axisLine={props.axisLine === false ? false : true}
                tickLine={props.tickLine === false ? false : true}
                tickFormatter={(value: number) => formatNumber(value)}
                label={{ ...label, position: 'insideLeft', angle: props.angle !== undefined ? +props.angle : -90 }}
                unit={props.unit}
                {...(props.style || {})}
            />
        );
    }
}
